/**
 * SPELL QUEUEING (#797) — hold a cast pressed in the last few ticks of a
 * cast/GCD/cooldown and fire it on the first legal tick.
 *
 * THE PROBLEM: a cast input that arrives one tick early is DROPPED by the sim
 * (`cast.ts` returns `{ ok: false, reason }` and nothing retains it), and at
 * `tickRate: 30` a cast is legal on exactly ONE tick and not the one before
 * it. The only way to chain abilities was to mash the key until a press
 * happened to land on that tick.
 *
 * THE FIX, ENTIRELY CLIENT-SIDE: hold the press for a few ticks and send it
 * when the gate opens. NO protocol, sim, content, hashed-state or
 * `SIM_VERSION` change — the queue changes WHEN an input is delivered, never
 * WHETHER it is legal. Every gate in `cast.ts` still runs, unchanged, at fire
 * time; a queued cast that lands late (latency) fizzles exactly as it does
 * today, which is what makes this safe.
 *
 * MODELLED ON WoW's `SpellQueueWindow`, which is what the owner described:
 *   • DEPTH EXACTLY 1 — a second press inside the window REPLACES the pending
 *     ability, it never stacks. A backlog would fire abilities the player no
 *     longer wants and would feel laggy, not responsive.
 *   • WINDOW-GATED — only a press within {@link SPELL_QUEUE_WINDOW_MS_DEFAULT}
 *     ms of the gate opening queues. A press with two seconds left is a
 *     MIS-PRESS, not intent: it must still fizzle, or the player loses the
 *     ability to cancel by simply not pressing again.
 *   • FIRES ON THE FIRST LEGAL TICK, via the shared timing math in
 *     `castReadiness.ts` (the same gates `ActionBar`'s cooldown sweep reads).
 *   • FIVE INVALIDATION RULES — a queued cast that fires under conditions the
 *     player didn't intend is worse than no queue at all. See {@link
 *     SpellQueue.poll}: different-ability press (replace), cast interrupted,
 *     death, match end, and the gate slipping past its predicted tick.
 *
 * TIMING ONLY (#797 Q3): the queue never predicts resource/school-lock/target
 * validity at fire time. It sends, and the sim's own gates decide.
 *
 * Pure and deterministic: no clock, no timers, no DOM, no store. The caller
 * (`InputSurface`) drives it with the freshest snapshot it has.
 */

import type { SimInput, WireSimState } from "@pit/protocol";
import { castReadyTick, type ReadinessEntity } from "./castReadiness.js";
import { ownEntity } from "../ui/hud-util.js";

/** The `cast` arm of the input union — the only kind this queue ever holds. */
export type CastInput = Extract<SimInput, { kind: "cast" }>;

/**
 * Default queue window, MILLISECONDS. 100ms — the owner's "last .1 second",
 * and exactly 3 ticks at `tickRate: 30` (33.333ms/tick).
 *
 * Stored/configured in ms (matching WoW's `SpellQueueWindow` and how the
 * feature was specified); converted to whole ticks ONCE, at the boundary, by
 * {@link spellQueueWindowTicks}.
 */
export const SPELL_QUEUE_WINDOW_MS_DEFAULT = 100;

/**
 * Minimum window, ms. `0` is a LEGAL value and means THE FEATURE IS OFF — no
 * press ever queues and behaviour is identical to pre-#797. This is the
 * built-in kill switch (never "queue forever", never a divide-by-zero).
 */
export const SPELL_QUEUE_WINDOW_MS_MIN = 0;

/**
 * Maximum window, ms. 210ms is the owner-specified ceiling; beyond it a queue
 * stops feeling like input assistance and starts firing abilities the player
 * has changed their mind about.
 */
export const SPELL_QUEUE_WINDOW_MS_MAX = 210;

/** Client-side tick rate (`tuning.json`'s `tickRate: 30`), matching every
 *  other client-side approximation (`InputSurface`, `cursorTracker`). */
export const SPELL_QUEUE_TICK_RATE_HZ = 30;

/**
 * Clamp a configured window into `[0, 210]` ms. Out-of-range input is CLAMPED
 * TO THE BOUND (not rejected, not defaulted) so a typo'd 5000 becomes 210
 * rather than a five-second queue; a non-finite value degrades to the default.
 */
export function clampSpellQueueWindowMs(ms: number): number {
  if (!Number.isFinite(ms)) return SPELL_QUEUE_WINDOW_MS_DEFAULT;
  if (ms < SPELL_QUEUE_WINDOW_MS_MIN) return SPELL_QUEUE_WINDOW_MS_MIN;
  if (ms > SPELL_QUEUE_WINDOW_MS_MAX) return SPELL_QUEUE_WINDOW_MS_MAX;
  return ms;
}

/**
 * Convert a window in ms to WHOLE TICKS.
 *
 * ROUNDING RULE — FLOOR, stated here rather than left to happen by accident:
 * a partial tick cannot be acted on, so 210ms (6.3 ticks at 30 Hz) is SIX
 * ticks, not seven. The two anchor values: 0ms → 0 ticks (feature off),
 * 100ms → exactly 3 ticks.
 *
 * The input is clamped first, so this is total for any number.
 */
export function spellQueueWindowTicks(
  ms: number,
  tickRateHz: number = SPELL_QUEUE_TICK_RATE_HZ,
): number {
  return Math.floor((clampSpellQueueWindowMs(ms) * tickRateHz) / 1000);
}

/**
 * The largest window the clamp allows, in WHOLE TICKS — `6` at 30 Hz (210ms).
 * The settings slider's upper bound (#826): the control is calibrated in
 * TICKS, because ticks are the only granularity the queue can act on.
 */
export const SPELL_QUEUE_WINDOW_MAX_TICKS = spellQueueWindowTicks(SPELL_QUEUE_WINDOW_MS_MAX);

/**
 * The window in MS to store for a window of exactly `ticks` whole ticks — the
 * inverse of {@link spellQueueWindowTicks}, and what makes the #826 settings
 * slider TICK-ALIGNED rather than a millisecond dial with dead zones.
 *
 * WHY THIS EXISTS: `spellQueueWindowTicks` FLOORS, so most millisecond values
 * are indistinguishable in behaviour — 100ms and 130ms are both exactly 3
 * ticks. A raw-ms slider would therefore have the player dragging through
 * ~30ms of nothing between each real change, and would let them save a value
 * (133ms) that reads back as a different one's behaviour. Every position on a
 * tick-calibrated slider changes the queue by exactly one tick.
 *
 * ROUNDING RULE — CEIL, the exact counterpart of the floor above: this returns
 * the SMALLEST ms that floors back to `ticks`, so the round trip
 * `spellQueueWindowTicks(spellQueueWindowMsForTicks(n)) === n` holds for every
 * `n` in `[0, SPELL_QUEUE_WINDOW_MAX_TICKS]` (asserted in `spellQueue.test.ts`).
 * The two anchors stay exact: 0 ticks → 0ms (feature off), 3 ticks → 100ms
 * (the shipped default).
 *
 * Out-of-range `ticks` is CLAMPED to the tick bounds (mirroring
 * `clampSpellQueueWindowMs`'s clamp-don't-reject rule); a non-finite value
 * degrades to the default window, same as the clamp does. Pure.
 */
export function spellQueueWindowMsForTicks(
  ticks: number,
  tickRateHz: number = SPELL_QUEUE_TICK_RATE_HZ,
): number {
  if (!Number.isFinite(ticks)) return SPELL_QUEUE_WINDOW_MS_DEFAULT;
  const maxTicks = spellQueueWindowTicks(SPELL_QUEUE_WINDOW_MS_MAX, tickRateHz);
  const whole = Math.min(maxTicks, Math.max(0, Math.floor(ticks)));
  return clampSpellQueueWindowMs(Math.ceil((whole * 1000) / tickRateHz));
}

/**
 * The cast/channel occupying the caster's main track when a press was queued
 * — the discriminator for the "interrupted" invalidation rule. `null` when the
 * player was merely on cooldown/GCD with nothing casting.
 */
interface CastSignature {
  readonly startTick: number;
  readonly endTick: number;
}

/** The single held press. Depth 1 — there is never a second one. */
export interface PendingCast {
  /** The exact input to send, captured at PRESS time (slot + skillshot aim).
   *  Aim is deliberately not re-derived at fire time: the pending state lives
   *  for at most a handful of ticks, and re-running the mapper would make the
   *  fired cast differ from the one the player aimed. */
  readonly input: CastInput;
  /** The tick the press was predicted to become legal — the fire tick, and
   *  the expiry deadline (see {@link SpellQueue.poll}). */
  readonly fireAtTick: number;
  /** The cast in progress when the press was held, or `null`. */
  readonly castAtPress: CastSignature | null;
}

/** What the caller must do with a press. */
export type PressDecision =
  /** Send it now — today's behaviour, untouched (legal now, outside the
   *  window, feature off, or nothing known well enough to judge). */
  | "sendNow"
  /** Held. Send NOTHING now; {@link SpellQueue.poll} will hand it back on the
   *  tick it becomes legal. */
  | "queued";

/**
 * The single-slot pending-cast buffer. One instance per `InputSurface` mount.
 */
export class SpellQueue {
  /** Window in whole ticks — `0` disables the queue outright. */
  readonly windowTicks: number;
  private pending: PendingCast | null = null;

  constructor(windowMs: number = SPELL_QUEUE_WINDOW_MS_DEFAULT, tickRateHz?: number) {
    this.windowTicks = spellQueueWindowTicks(windowMs, tickRateHz);
  }

  /** The held press, or `null`. Read-only view for tests/diagnostics. */
  peek(): PendingCast | null {
    return this.pending;
  }

  /** Drop the held press. Idempotent. */
  clear(): void {
    this.pending = null;
  }

  /**
   * Judge a cast keypress against the freshest snapshot available.
   *
   * ANY press supersedes a held one first (depth 1, replace-not-stack) —
   * including one that sends immediately, so a "cast now" press can never
   * leave a stale queued ability behind it.
   *
   * Unknown/absent inputs resolve to `"sendNow"`: this buffer exists to
   * re-time a press it can prove is early, never to swallow one it can't
   * judge (mirrors `castability.ts`'s "explain a CERTAIN failure, never guess
   * at one" contract).
   */
  press(input: CastInput, snapshot: WireSimState | null, ownSeat: number | null): PressDecision {
    this.pending = null;
    if (this.windowTicks <= 0) return "sendNow"; // kill switch — pre-#797 exactly
    if (snapshot?.phase !== "active") return "sendNow";
    const me = ownEntity(snapshot, ownSeat);
    if (me?.alive !== true) return "sendNow";

    const fireAtTick = castReadyTick(me, input.slot);
    if (fireAtTick <= snapshot.tick) return "sendNow"; // legal now
    if (fireAtTick - snapshot.tick > this.windowTicks) return "sendNow"; // mis-press: fizzles as today

    this.pending = {
      input,
      fireAtTick,
      castAtPress:
        me.cast === null ? null : { startTick: me.cast.startTick, endTick: me.cast.endTick },
    };
    return "queued";
  }

  /**
   * Re-evaluate the held press against a fresh snapshot. Returns the input to
   * send NOW (and drops it), or `null` to keep waiting / after invalidating.
   *
   * Rule order matters — invalidation is checked BEFORE firing:
   *   1. DEATH / MATCH END / entity gone → drop. A queued cast must never
   *      survive the fight it was queued in.
   *   2. INTERRUPT → drop. The cast we were waiting behind vanished before its
   *      `endTick`, or a different cast took the track: the situation the
   *      player queued into no longer exists.
   *   3. GATE OPEN (re-derived from THIS snapshot, never the stale press-time
   *      prediction) → fire.
   *   4. GATE SLIPPED past the predicted tick → drop. This is the "window
   *      expired without the gate opening" rule, and it is what stops a
   *      pending cast from lingering indefinitely.
   */
  poll(snapshot: WireSimState | null, ownSeat: number | null): CastInput | null {
    const p = this.pending;
    if (p === null) return null;
    if (snapshot === null) return null; // nothing new to judge — keep holding
    const me = ownEntity(snapshot, ownSeat);
    if (me?.alive !== true || snapshot.phase !== "active") {
      this.pending = null;
      return null;
    }
    if (interrupted(p.castAtPress, me.cast, snapshot.tick)) {
      this.pending = null;
      return null;
    }
    const readyTick = castReadyTick(me, p.input.slot);
    if (readyTick <= snapshot.tick) {
      this.pending = null;
      return p.input;
    }
    if (readyTick > p.fireAtTick) {
      this.pending = null;
      return null;
    }
    return null;
  }
}

/**
 * Did the cast we were queued behind get interrupted or replaced?
 *
 * A cast that simply COMPLETED is not an interrupt: at `tick >= endTick` the
 * track is empty because the cast resolved, which is precisely the moment the
 * queued press is waiting for. Only a track that emptied EARLY, or now holds a
 * DIFFERENT cast, invalidates.
 */
function interrupted(
  atPress: CastSignature | null,
  now: ReadinessEntity["cast"],
  tick: number,
): boolean {
  if (atPress === null) return false;
  if (now === null) return tick < atPress.endTick;
  return now.startTick !== atPress.startTick;
}
